'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldCheck, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';

export interface AttestationModalProps {
  content2257RecordId: number;
  detectedFaceCount: number;
  imageId: number;
  onClose: () => void;
  onSuccess: (result: AttestationSubmitResult) => void;
}

export interface AttestationSubmitResult {
  status: 'published';
  contentType: string;
  contentId: number;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AttestationModal({
  content2257RecordId,
  detectedFaceCount,
  imageId,
  onClose,
  onSuccess,
}: AttestationModalProps) {
  const isMulti = detectedFaceCount > 1;

  // Canonical attestation text is fetched from the backend so the text the
  // user agreed to and the text the server validates on submit are anchored
  // to the same constant. If they ever drift, submit is refused.
  const [attestationText, setAttestationText] = useState<string>('');
  const [attestationVersion, setAttestationVersion] = useState<string>('');
  const [textLoading, setTextLoading] = useState(true);

  const [shootDate, setShootDate] = useState<string>(todayIsoDate());
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch('/api/content/attestation-current-text');
        if (!resp.ok) throw new Error(`Failed to load attestation text (${resp.status})`);
        const body = (await resp.json()) as { version: string; text: string };
        if (cancelled) return;
        setAttestationText(body.text);
        setAttestationVersion(body.version);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load attestation text.');
      } finally {
        if (!cancelled) setTextLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function validate(): string | null {
    if (!attestationText || !attestationVersion) return 'Attestation text is still loading. Please wait.';
    if (!shootDate) return 'Shoot date is required.';
    const sd = new Date(shootDate);
    if (Number.isNaN(sd.getTime())) return 'Shoot date is invalid.';
    if (sd > new Date()) return 'Shoot date cannot be in the future.';
    if (!attested) return 'You must check the attestation to submit.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const resp = await apiFetch(`/api/content/${content2257RecordId}/complete-attestation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shootDate,
          attestationText,
          attestationVersion,
          performers: [],
        }),
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Submit failed (${resp.status})`);
      }

      const result = (await resp.json()) as AttestationSubmitResult;
      onSuccess(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 grid place-items-center p-4">
      <div className="bg-background text-foreground w-full max-w-lg rounded-xl border shadow-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Attestation required</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isMulti
                  ? `${detectedFaceCount} performers detected — confirm you hold §2257 records for each.`
                  : 'Confirm you hold §2257 records for the depicted performer.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-2 space-y-5 overflow-y-auto">
          {/* Shoot date */}
          <div>
            <label htmlFor="att-shoot-date" className="block text-sm font-semibold mb-1">
              When was this content recorded? <span className="text-red-600">*</span>
            </label>
            <input
              id="att-shoot-date"
              type="date"
              value={shootDate}
              onChange={(e) => setShootDate(e.target.value)}
              max={todayIsoDate()}
              required
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Required for §2257 records. Cannot be in the future.
            </p>
          </div>

          {/* Attestation */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            {textLoading ? (
              <p className="text-sm text-amber-900 dark:text-amber-200">Loading attestation text…</p>
            ) : (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                  disabled={submitting || !attestationText}
                  className="mt-1 h-4 w-4 rounded border-input"
                />
                <span className="text-sm text-amber-900 dark:text-amber-200 whitespace-pre-line">{attestationText}</span>
              </label>
            )}
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={submitting || !attested || textLoading || !attestationText}
            >
              {submitting ? 'Submitting…' : 'Submit attestation & publish'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel — content stays in review
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
